// Share / revoke the public link for one Captain's Log trip.
//
// ShareTripModal's sibling over the same ShareLinkModal machine. Three things
// differ, and all of them are about what this link does rather than about the
// dialog:
//
//   - IT CARRIES THE PHOTOS. Every entry's attached photos are served to the
//     viewer as presigned S3 URLs, which no other share does, so the copy says
//     so. The Captain column is free text and routinely holds someone else's
//     name, so that is said too -- the alternative, publishing a blank column,
//     would misrepresent the owner's own table.
//
//   - IT STORES AN identityId. The Lambda finds the photos under
//     `private/<identityId>/`, and that id is minted by GetId against the
//     user's own token -- it is not derivable server-side, so it is captured
//     here at share time. A failure to resolve it is NOT fatal: the right
//     outcome is a link with no photos, not a failed link.
//
//   - IT CAN ACCEPT PHOTOS BACK. Anyone holding the link can add their own to a
//     day of the trip, and the owner is emailed when they do. That is the one
//     write a public page in this app can make, so it gets an explicit control
//     here and the copy leads with it -- a link that accepts uploads is a
//     materially different thing to hand out than a read-only one, and the
//     dialog is the only place that can say so before it is handed out.
//
// TWO VALUES ARE CAPTURED AT SHARE TIME, both best-effort and for the same reason:
// the identityId (above) and the address the notification goes to. Neither can be
// resolved server-side -- nothing in amplify/ can turn an `owner` claim into an
// email -- and neither failing should cost the user a working link.
//
// `fetchUserAttributes()` is called directly rather than threading a `user` prop
// down from App.jsx the way TripGroup does for WeatherEmailModal: LogbookTrip is
// not handed one, and plumbing a single string through the whole item-page route
// tree to get it here would be a lot of surface for a default.

import { useCallback, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { fetchUserAttributes } from 'aws-amplify/auth';
import ShareLinkModal from '../trip/ShareLinkModal';
import {
  createLogbookTripShare,
  getLogbookTripShareForKey,
  deleteLogbookTripShare,
  updateLogbookTripShareUploads,
} from '../../utilities/Database.mjs';
import { warmAuthSession } from '../../auth/sessionWarm';
import { fullUrlForSharedLogbookTrip } from '../../fleet-shared/Urls.mjs';
import { shareUploadAvailable } from '../../utilities/shareUpload.mjs';

const DESCRIPTION = `Anyone with the link can view this trip's entries, notes, photos and
        map — no account needed, and they can add their own photos to any day of
        the trip. Photo links refresh each time the page is opened and stop
        working about an hour later. You can revoke the link here at any time.`;

// The generic dialog's default says "read-only", which is true of a planned-trip
// share and no longer true of this one. Overridden rather than made conditional:
// ShareLinkModal knows nothing about what its caller's link can do.
const ACTIVE_INTRO =
  'Anyone with this link can view this trip and add photos to it — no account needed:';

const ACTIVE_NOTE =
  "The link shows the trip's current state, including edits made after sharing. " +
  'Anything in these entries is visible to whoever holds it, including the ' +
  "Captain column and any names in your notes.";

export default function ShareLogbookTripModal({
  show,
  onHide,
  itemId,
  logbookId,
  tripKey,
  tripName,
}) {
  const load = useCallback(
    () => getLogbookTripShareForKey(logbookId, tripKey),
    [logbookId, tripKey]
  );

  const create = useCallback(async () => {
    // Both best-effort and deliberately not awaited behind a guard that could
    // fail the create: a share with no photos is a working share, and so is one
    // that cannot tell you about an upload.
    const session = await warmAuthSession().catch(() => null);
    const attributes = await fetchUserAttributes().catch(() => null);

    return createLogbookTripShare({
      itemId,
      logbookId,
      tripKey,
      identityId: session?.identityId ?? null,
      // NOT gated on shareUploadAvailable(), deliberately: the mutation's own
      // selection set already names these columns, so a backend that predates them
      // rejects this create whether or not the values are sent. Gating the values
      // too would buy nothing and leave two code paths where one will do. The gate
      // guards the CONTROL below, and the update call behind it.
      allowUploads: true,
      notifyEmail: attributes?.email ?? null,
    });
  }, [itemId, logbookId, tripKey]);

  const revoke = useCallback((share) => deleteLogbookTripShare(share.id), []);
  const urlFor = useCallback((share) => fullUrlForSharedLogbookTrip(share.id), []);

  // Only offered once a link exists: there is nothing to configure about a link
  // that has not been created, and `create` already turns uploads on.
  const extras = useCallback(
    (share) => (share && shareUploadAvailable() ? <UploadToggle share={share} /> : null),
    []
  );

  return (
    <ShareLinkModal
      show={show}
      onHide={onHide}
      title={`Share ${tripName || 'Trip'}`}
      description={DESCRIPTION}
      activeIntro={ACTIVE_INTRO}
      activeNote={ACTIVE_NOTE}
      load={load}
      create={create}
      revoke={revoke}
      urlFor={urlFor}
      extras={extras}
    />
  );
}

// "Let visitors add photos", and the address the mail goes to.
//
// OPTIMISTIC, and it has to be: the switch is the whole feedback, so waiting for a
// round trip before moving it reads as a dead control. A failed write puts it back
// and says so, which is the honest version of the same thing.
//
// `allowUploads !== false` is the read rule, spelled here exactly as the Lambda
// spells it: the column is nullish on every row written before it existed, and
// those links accept photos.
function UploadToggle({ share }) {
  const [allowed, setAllowed] = useState(share.allowUploads !== false);
  const [error, setError] = useState(null);

  const change = async (next) => {
    setAllowed(next);
    setError(null);
    try {
      await updateLogbookTripShareUploads(share.id, { allowUploads: next });
    } catch (err) {
      setAllowed(!next);
      setError(err);
    }
  };

  return (
    <div className="border-top mt-3 pt-3">
      <Form.Check
        type="switch"
        id="share-logbook-allow-uploads"
        label="Let visitors add photos to this trip"
        checked={allowed}
        onChange={(e) => change(e.target.checked)}
      />
      <p className="text-muted small mb-0 mt-2">
        {allowed
          ? share.notifyEmail
            ? `Anyone with the link can add photos to a day of this trip. We'll email ${share.notifyEmail} when they do, and you can delete any of them from the entry.`
            : "Anyone with the link can add photos to a day of this trip. We don't have an email address for this link, so you won't be notified — you'll see them on the trip's entries."
          : 'The link is read-only. Nobody can add photos to this trip.'}
      </p>
      {error && (
        <p className="text-danger small mb-0 mt-2">
          Couldn&apos;t change that just now. Please try again.
        </p>
      )}
    </div>
  );
}
